import { useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import SEO from '../../components/SEO';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bodhira.online';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/contact`,
      url: `${siteUrl}/contact`,
      name: 'Contact Us | Bodhira',
      description: 'Get in touch with Bodhira. Reach out with questions about our botanical candles, ritual sprays, yin yoga classes, or wholesale inquiries.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Contact', item: `${siteUrl}/contact` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${siteUrl}/contact#contactpage`,
      name: 'Contact Bodhira',
      description: 'Get in touch with Bodhira for product questions, yoga inquiries, wholesale, or just to say hello.',
      url: `${siteUrl}/contact`,
      mainEntity: {
        '@type': 'Organization',
        name: 'Bodhira',
        url: siteUrl,
        sameAs: ['https://instagram.com/bodhira.co'],
        address: {
          '@type': 'PostalAddress',
          addressRegion: 'NJ',
          addressCountry: 'US',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+19086707882',
          contactType: 'customer service',
          areaServed: 'US',
          availableLanguage: 'English',
        },
      },
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    Array.from(form.elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (input.name) {
        if (input.type === 'checkbox') {
          if ((input as HTMLInputElement).checked) data.append(input.name, input.value);
        } else {
          data.append(input.name, input.value);
        }
      }
    });
    try {
      await fetch('https://readdy.ai/api/form/d6nopso97e9r3849v2gg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us | Bodhira"
        description="Get in touch with Bodhira. Reach out with questions about our botanical candles, ritual sprays, yin yoga classes, or wholesale inquiries."
        canonical="/contact"
        ogTitle="Contact Bodhira"
        ogDescription="Reach out to Bodhira — we'd love to hear from you."
        keywords="contact Bodhira, botanical candles inquiry, yin yoga questions, wellness brand contact, New Jersey"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Banner */}
        <div className="relative w-full h-64 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/5e5e32ca6183abe1d8a730c09c9f89ae.jpg"
            alt="Contact Bodhira Co"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span className="font-sans text-xs tracking-widest uppercase text-cream-100/80 mb-3">Bodhira Co</span>
            <h1 className="font-serif text-5xl text-white font-semibold">Get in Touch</h1>
            <p className="font-sans text-sm text-cream-100/90 mt-3">We'd love to hear from you — reach out anytime.</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

            {/* Left — Info */}
            <div className="lg:col-span-2 flex flex-col gap-10">
              <div>
                <span className="inline-block px-4 py-1.5 bg-sage-400/20 text-sage-600 rounded-full text-xs font-sans font-medium tracking-widest uppercase mb-4">
                  Contact Info
                </span>
                <h2 className="font-serif text-3xl text-sage-700 leading-snug mb-3">
                  Say Hello
                </h2>
                <div className="w-10 h-0.5 bg-sage-400 rounded-full mb-5"></div>
                <p className="font-sans text-sm text-gray-600 leading-relaxed">
                  Whether you have a question about a product, or just want to connect — I'm here and happy to help. I personally read every message.
                </p>
              </div>

              {/* Contact Details */}
              <div className="flex flex-col gap-5">
                {[
                  {
                    icon: 'ri-instagram-line',
                    label: 'Instagram',
                    value: '@bodhira.co',
                    href: 'https://instagram.com/bodhira.co',
                  },
                  {
                    icon: 'ri-map-pin-line',
                    label: 'Location',
                    value: 'New Jersey, USA',
                    href: null,
                  },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4">
                    <div>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.href.startsWith('http') ? '_blank' : undefined}
                          rel={item.href.startsWith('http') ? 'noopener noreferrer nofollow' : undefined}
                          className="font-sans text-sm text-sage-700 font-medium hover:text-sage-500 transition-colors cursor-pointer"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-sans text-sm text-sage-700 font-medium">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Response Time */}
              <div className="bg-cream-50 rounded-2xl p-6 border border-sage-100/60">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-sage-400/20 rounded-full">
                    <i className="ri-time-line text-base text-sage-600"></i>
                  </div>
                  <h4 className="font-serif text-base text-sage-700">Response Time</h4>
                </div>
                <p className="font-sans text-sm text-gray-500 leading-relaxed">
                  I typically respond within <strong className="text-sage-600">1–2 business days</strong>. For urgent matters, DM me on Instagram for a faster reply.
                </p>
              </div>
            </div>

            {/* Right — Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-3xl border border-sage-100 shadow-sm p-10">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center text-center py-16 gap-5">
                    <div className="w-16 h-16 flex items-center justify-center bg-sage-400/20 rounded-full">
                      <i className="ri-check-line text-3xl text-sage-600"></i>
                    </div>
                    <h3 className="font-serif text-3xl text-sage-700">Message Sent!</h3>
                    <p className="font-sans text-sm text-gray-500 max-w-sm leading-relaxed">
                      Thank you for reaching out. I'll get back to you within 1–2 business days. In the meantime, feel free to follow along on Instagram.
                    </p>
                    <a
                      href="https://instagram.com/bodhira.co"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-2 bg-sage-500 text-white px-6 py-2.5 rounded-full font-sans text-sm font-medium hover:bg-sage-600 transition-colors whitespace-nowrap cursor-pointer mt-2"
                    >
                      <i className="ri-instagram-line"></i>
                      @bodhira.co
                    </a>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="font-sans text-xs text-gray-400 hover:text-sage-600 transition-colors cursor-pointer mt-1 whitespace-nowrap"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-8">
                      <h2 className="font-serif text-2xl text-sage-700 mb-1">Send a Message</h2>
                      <p className="font-sans text-sm text-gray-400">Fill out the form below and I'll be in touch soon.</p>
                    </div>

                    <form
                      id="contact-form"
                      data-readdy-form
                      onSubmit={handleSubmit}
                      className="flex flex-col gap-5"
                    >
                      {/* Name + Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="name" className="font-sans text-xs font-medium text-gray-500 tracking-wide uppercase">
                            Full Name <span className="text-rose-400">*</span>
                          </label>
                          <input
                            id="name"
                            name="name"
                            type="text"
                            required
                            placeholder="Taylor Smith"
                            className="w-full border border-sage-200 rounded-xl px-4 py-3 font-sans text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-sage-400 focus:ring-1 focus:ring-sage-300 transition bg-cream-50/50"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <label htmlFor="email" className="font-sans text-xs font-medium text-gray-500 tracking-wide uppercase">
                            Email Address <span className="text-rose-400">*</span>
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            placeholder="you@example.com"
                            className="w-full border border-sage-200 rounded-xl px-4 py-3 font-sans text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-sage-400 focus:ring-1 focus:ring-sage-300 transition bg-cream-50/50"
                          />
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="subject" className="font-sans text-xs font-medium text-gray-500 tracking-wide uppercase">
                          Subject <span className="text-rose-400">*</span>
                        </label>
                        <select
                          id="subject"
                          name="subject"
                          required
                          defaultValue=""
                          className="w-full border border-sage-200 rounded-xl px-4 py-3 font-sans text-sm text-gray-700 focus:outline-none focus:border-sage-400 focus:ring-1 focus:ring-sage-300 transition bg-cream-50/50 cursor-pointer"
                        >
                          <option value="" disabled>Select a topic…</option>
                          <option value="Product Question">Product Question</option>
                          <option value="Order & Shipping">Order &amp; Shipping</option>
                          <option value="Yoga">Yoga</option>
                          <option value="Wholesale Inquiry">Wholesale Inquiry</option>
                          <option value="Collaboration">Collaboration</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {/* Message */}
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="message" className="font-sans text-xs font-medium text-gray-500 tracking-wide uppercase">
                          Message <span className="text-rose-400">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={6}
                          maxLength={500}
                          placeholder="Tell me what's on your mind…"
                          onChange={(e) => setCharCount(e.target.value.length)}
                          className="w-full border border-sage-200 rounded-xl px-4 py-3 font-sans text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-sage-400 focus:ring-1 focus:ring-sage-300 transition bg-cream-50/50 resize-none"
                        />
                        <p className={`font-sans text-xs text-right ${charCount >= 500 ? 'text-rose-400' : 'text-gray-400'}`}>
                          {charCount}/500
                        </p>
                      </div>

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={submitting || charCount > 500}
                        className="w-full bg-sage-500 hover:bg-sage-600 disabled:opacity-60 text-white font-sans text-sm font-medium py-3.5 rounded-xl transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 mt-1"
                      >
                        {submitting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-base"></i>
                            Sending…
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line text-base"></i>
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
